import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Box, Divider, IconButton } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { LocationNavigationHeader } from "../../components/LocationNavigationHeader";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { Hangar, Location, Ramp } from "../../types";

interface LayoutState {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
}

export const useLayout = create(
  persist<LayoutState>(
    (set) => ({
      isDrawerOpen: true,
      setIsDrawerOpen: (isOpen) => set({ isDrawerOpen: isOpen }),
    }),
    {
      name: "layout-storage", // name of the item in storage
    }
  )
);

type Props = {
  location: Location<Ramp | Hangar>;
  children: React.ReactNode;
  onClickBack?: () => void;
  hideTitle?: boolean;
  hideNavigation?: boolean;
  extendBorderUp?: boolean;
};

export const CollapsibleRampActionCard: React.FC<Props> = ({
  location,
  children,
  onClickBack,
  hideTitle = false,
  hideNavigation = false,
  extendBorderUp = false,
}) => {
  const { t } = useTranslation();
  const { isDrawerOpen, setIsDrawerOpen } = useLayout();
  const { activeFBO } = useActiveFBOs();
  const drawerWidth = 400;
  const miniDrawerWidth = 125;

  // Check if scheduling is enabled
  const isSchedulingEnabled = activeFBO?.settings?.enableScheduling ?? false;

  let namePrefix = "";
  if (location.stack.isReference) {
    if (location.type === "ramp") {
      namePrefix = t('screens.ramp.namePrefixes.allTieDownTenants');
    } else if (location.type === "hangar") {
      namePrefix = t('screens.ramp.namePrefixes.allTenants');
    }
  }

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        height="100vh"
        width={isDrawerOpen ? drawerWidth : miniDrawerWidth}
        sx={{
          overflow: "hidden",
          transition: "width 0.3s",
          borderRight: 1,
          borderTop: extendBorderUp ? 1 : 0,
          borderLeft: 0,
          borderColor: "divider",
        }}
      >
        {!hideNavigation && isDrawerOpen && (
          <Box sx={{ p: 1 }}>
            {!hideTitle ? (
              <LocationNavigationHeader
                locationName={`${namePrefix}${location.name}`}
                locationId={location.id}
                locationType={location.type}
                isSchedulingEnabled={isSchedulingEnabled}
                onBack={onClickBack}
                rightActions={
                  <IconButton onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
                    <KeyboardDoubleArrowLeftIcon />
                  </IconButton>
                }
              />
            ) : (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
                  <KeyboardDoubleArrowLeftIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
        {!hideNavigation && !isDrawerOpen && (
          <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
            <IconButton onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
              <KeyboardDoubleArrowRightIcon />
            </IconButton>
          </Box>
        )}
        {!hideNavigation && <Divider />}
        <Box display="flex" flex={1} overflow="hidden">
          {children}
        </Box>
      </Box>
    </Box>
  );
};
